import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaNewspaper,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaPlus,
  FaExternalLinkAlt,
  FaEdit,
  FaEye,
  FaDatabase,
  FaServer,
  FaArrowRight,
} from 'react-icons/fa';
import { apiFetch, API_BASE_URL } from '../../utils/api';

export default function DashboardPage() {
  const [blogs, setBlogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || 'null');
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDashboardData = async () => {
      try {
        setLoading(true);

        const [blogsRes, usersRes] = await Promise.allSettled([
          apiFetch('/api/blogs/all'),
          apiFetch('/api/users/all'),
        ]);

        if (isMounted) {
          if (blogsRes.status === 'fulfilled' && blogsRes.value.ok) {
            const blogData = await blogsRes.value.json();
            setBlogs(Array.isArray(blogData) ? blogData : []);
          }

          if (usersRes.status === 'fulfilled' && usersRes.value.ok) {
            const userData = await usersRes.value.json();
            setUsers(Array.isArray(userData) ? userData : []);
          }

          setError('');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const totalBlogs = blogs.length;
    const publishedBlogs = blogs.filter(
      (blog) => String(blog.status).toLowerCase() === 'published'
    ).length;
    const draftBlogs = blogs.filter(
      (blog) => String(blog.status).toLowerCase() === 'draft'
    ).length;
    const recentBlogs = [...blogs]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);

    // Categories breakdown
    const categoryCounts = {};
    blogs.forEach((b) => {
      const cat = b.category || 'General';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });

    return {
      totalBlogs,
      publishedBlogs,
      draftBlogs,
      totalUsers: users.length,
      recentBlogs,
      categoryCounts: Object.entries(categoryCounts).slice(0, 4),
    };
  }, [blogs, users]);

  const cards = [
    {
      label: 'Total Blogs',
      value: stats.totalBlogs,
      subtext: `${stats.publishedBlogs} published`,
      icon: FaNewspaper,
      tone: 'bg-blue-50 text-blue-600 border-blue-100',
    },
    {
      label: 'Published Articles',
      value: stats.publishedBlogs,
      subtext: 'Live on website',
      icon: FaCheckCircle,
      tone: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    },
    {
      label: 'Draft Articles',
      value: stats.draftBlogs,
      subtext: 'In review / editing',
      icon: FaClock,
      tone: 'bg-amber-50 text-amber-600 border-amber-100',
    },
    {
      label: 'System Users',
      value: stats.totalUsers || 1,
      subtext: 'Admins & editors',
      icon: FaUsers,
      tone: 'bg-purple-50 text-purple-600 border-purple-100',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-r from-[#01295c] via-[#002f63] to-[#2596be] p-6 sm:p-8 text-white shadow-sm">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-amber-300 backdrop-blur-xs mb-3">
              <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              Finegal Control Panel
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Welcome back, {currentUser?.name || 'Administrator'} 👋
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-white/80 max-w-xl">
              Manage your blog publications, review drafts, oversee team accounts, and configure system preferences.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/admin/blogs/create"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-4 py-2.5 text-xs font-bold text-slate-950 transition hover:bg-amber-300 shadow-sm"
            >
              <FaPlus className="text-xs" />
              <span>Write Blog</span>
            </Link>
            <Link
              to="/blogs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-white/20"
            >
              <FaExternalLinkAlt className="text-[10px]" />
              <span>Live Site</span>
            </Link>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-xs font-medium text-red-700">
          {error}
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ label, value, subtext, icon: Icon, tone }) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-2xs transition-all hover:shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
                <p className="mt-1.5 text-2xl font-black tracking-tight text-slate-900">{value}</p>
                <p className="mt-1 text-[11px] text-slate-400">{subtext}</p>
              </div>
              <div className={`rounded-xl border p-3 ${tone}`}>
                <Icon className="text-xl" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Recent Blogs Table (8 columns) */}
        <div className="lg:col-span-8 space-y-6">
          <div className="rounded-3xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-2xs">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900">Recent Articles</h2>
                <p className="text-xs text-slate-500">Latest publications and drafts</p>
              </div>
              <Link
                to="/admin/blogs"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#01295c] hover:text-amber-600 transition"
              >
                <span>View all</span>
                <FaArrowRight className="text-[9px]" />
              </Link>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 animate-pulse rounded-2xl bg-slate-100" />
                ))}
              </div>
            ) : stats.recentBlogs.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                <p className="text-xs font-medium text-slate-500 mb-3">No articles published yet.</p>
                <Link
                  to="/admin/blogs/create"
                  className="btn-primary py-2 px-4 rounded-xl text-xs font-bold inline-flex items-center gap-2"
                >
                  <FaPlus className="text-xs" />
                  Create First Article
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {stats.recentBlogs.map((blog) => {
                  const isPublished = String(blog.status).toLowerCase() === 'published';
                  return (
                    <div
                      key={blog._id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3.5 hover:bg-slate-50/60 transition rounded-xl px-2"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        {blog.featuredImage ? (
                          <img
                            src={blog.featuredImage}
                            alt=""
                            className="h-11 w-11 shrink-0 rounded-xl object-cover bg-slate-100"
                          />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#01295c] text-xs font-bold">
                            FT
                          </div>
                        )}
                        <div className="min-w-0">
                          <Link
                            to={`/admin/blogs/edit/${blog._id}`}
                            className="font-bold text-slate-800 text-sm hover:text-amber-600 transition line-clamp-1 block"
                          >
                            {blog.title}
                          </Link>
                          <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                            <span className="font-medium text-slate-600">
                              {blog.category || 'General'}
                            </span>
                            <span>•</span>
                            <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>{blog.readingTime || 3} min</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 sm:self-center">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                            isPublished
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {blog.status || 'Draft'}
                        </span>

                        <Link
                          to={`/admin/blogs/edit/${blog._id}`}
                          title="Edit Blog"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition"
                        >
                          <FaEdit className="text-xs" />
                        </Link>
                        <Link
                          to={`/blogs/${blog.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Preview Blog"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition"
                        >
                          <FaEye className="text-xs" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Quick Tools & System Overview (4 columns) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Quick Actions Card */}
          <div className="rounded-3xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-2xs">
            <h3 className="text-sm font-bold text-slate-900 mb-3">Quick Navigation</h3>
            <div className="space-y-2">
              <Link
                to="/admin/blogs/create"
                className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/70 p-3 text-xs font-semibold text-slate-800 transition hover:bg-slate-100 hover:border-slate-300"
              >
                <span className="flex items-center gap-2">
                  <FaPlus className="text-amber-500" /> Write New Blog Post
                </span>
                <FaArrowRight className="text-[10px] text-slate-400" />
              </Link>
              <Link
                to="/admin/users"
                className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/70 p-3 text-xs font-semibold text-slate-800 transition hover:bg-slate-100 hover:border-slate-300"
              >
                <span className="flex items-center gap-2">
                  <FaUsers className="text-blue-500" /> Manage Team Accounts
                </span>
                <FaArrowRight className="text-[10px] text-slate-400" />
              </Link>
              <Link
                to="/admin/settings"
                className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/70 p-3 text-xs font-semibold text-slate-800 transition hover:bg-slate-100 hover:border-slate-300"
              >
                <span className="flex items-center gap-2">
                  <FaServer className="text-emerald-500" /> System Settings
                </span>
                <FaArrowRight className="text-[10px] text-slate-400" />
              </Link>
            </div>
          </div>

          {/* System & Architecture Status Card */}
          <div className="rounded-3xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-2xs">
            <h3 className="text-sm font-bold text-slate-900 mb-3">System Health</h3>
            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-2.5">
                <span className="flex items-center gap-2 font-medium text-slate-600">
                  <FaDatabase className="text-emerald-500" /> Database
                </span>
                <span className="inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-100/60 px-2 py-0.5 rounded-md text-[10px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Atlas Cloud
                </span>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-2.5">
                <span className="flex items-center gap-2 font-medium text-slate-600">
                  <FaServer className="text-blue-500" /> API Server
                </span>
                <span className="font-semibold text-slate-800 truncate max-w-[140px]" title={API_BASE_URL}>
                  {API_BASE_URL.replace(/^https?:\/\//, '') || 'Localhost'}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-2.5">
                <span className="font-medium text-slate-600">Image Compression</span>
                <span className="font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md text-[10px]">
                  Sharp WebP (80%)
                </span>
              </div>
            </div>
          </div>

          {/* Categories Count */}
          {stats.categoryCounts.length > 0 && (
            <div className="rounded-3xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-2xs">
              <h3 className="text-sm font-bold text-slate-900 mb-3">Top Topics</h3>
              <div className="space-y-2">
                {stats.categoryCounts.map(([cat, count]) => (
                  <div key={cat} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{cat}</span>
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 font-bold text-slate-800 text-[11px]">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
